import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Ban, Download, Plus, ShoppingCart, Wallet } from 'lucide-react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUSES,
  PAYMENT_STATUS_LABELS,
  SALE_SOURCES,
  SALE_SOURCE_LABELS,
  type Paginated,
  type PaymentStatus,
  type Sale,
} from '@cenox/shared';
import { api, downloadFile, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { useModules } from '../../lib/modules';
import { date, money } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Pagination,
  Select,
  TableSkeleton,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { SaleForm } from './SaleForm';
import { RecordPaymentForm } from './RecordPaymentForm';

const STATUS_TONES: Record<PaymentStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  paid: 'success',
  partial: 'warning',
  unpaid: 'danger',
  refunded: 'neutral',
  void: 'neutral',
};

export function SalesPage(): ReactNode {
  const { business, can } = useAuth();
  // A pharmacy sells only at its tills, where stock, price, prescription and
  // payment rules apply; the server refuses a sale typed in here.
  const pharmacy = useModules().active.has('pharmacy');
  const mayRecord = can('sales:create') && !pharmacy;
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('paymentStatus') ?? '');
  const [source, setSource] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(
    searchParams.get('outstandingOnly') === 'true',
  );

  const [creating, setCreating] = useState(false);
  const [payingFor, setPayingFor] = useState<Sale | null>(null);
  const [voiding, setVoiding] = useState<Sale | null>(null);

  /**
   * The working location narrows the list.
   *
   * "All locations" sends no `branchId` at all, which is exactly what "do not
   * narrow" means to the API. Choosing a location here is a working context,
   * not a report — it answers "what is happening where I am", which is the
   * question somebody standing at one counter is actually asking.
   */
  const locationFilter = useLocationFilter();
  const locationReady = useLocationReady();

  const filters = qs({
    page,
    pageSize: 25,
    search: search || undefined,
    paymentStatus: status || undefined,
    source: source || undefined,
    outstandingOnly: outstandingOnly || undefined,
    ...locationFilter,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['sales', business?.businessId, filters],
    queryFn: () => api.get<Paginated<Sale>>(path(`/sales${filters}`)),
    // Wait for the working location, or the first request asks for the whole
    // business and the user watches the wrong answer go past.
    enabled: Boolean(business) && locationReady,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sales'] });
    // The dashboard's totals move whenever a sale does.
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['reports'] });
  };

  const voidSale = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(path(`/sales/${id}/void`), { reason }),
    onSuccess: () => {
      setVoiding(null);
      invalidate();
    },
  });

  const currency = business?.currencyCode ?? 'USD';
  const timezone = business?.timezone ?? 'UTC';

  return (
    <>
      <PageHeader
        title="Sales"
        description="Every sale recorded, from any source"
        action={
          <div className="flex gap-2">
            {can('reports:export') && (
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() =>
                  void downloadFile(
                    `/api/v1${path(`/exports${qs({ dataset: 'sales', format: 'xlsx', ...locationFilter })}`)}`,
                    'sales.xlsx',
                  )
                }
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            )}
            {mayRecord && (
              <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Record sale
              </Button>
            )}
            {pharmacy && can('pharmacy:sell') && (
              <Link to="/pharmacy/pos">
                <Button icon={<ShoppingCart className="size-4" />}>Sell at the till</Button>
              </Link>
            )}
          </div>
        }
      />

      <Card padded={false} className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder="Search description, reference, customer…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            aria-label="Search sales"
          />
          <Select
            placeholder="All statuses"
            options={PAYMENT_STATUSES.map((value) => ({
              value,
              label: PAYMENT_STATUS_LABELS[value],
            }))}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            aria-label="Filter by payment status"
          />
          <Select
            placeholder="All sources"
            options={SALE_SOURCES.map((value) => ({ value, label: SALE_SOURCE_LABELS[value] }))}
            value={source}
            onChange={(event) => {
              setSource(event.target.value);
              setPage(1);
            }}
            aria-label="Filter by source"
          />
          <label className="flex items-center gap-2 self-center text-sm text-slate-700">
            <input
              type="checkbox"
              checked={outstandingOnly}
              onChange={(event) => {
                setOutstandingOnly(event.target.checked);
                setPage(1);
                setSearchParams(event.target.checked ? { outstandingOnly: 'true' } : {});
              }}
              className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Unpaid only
          </label>
        </div>
      </Card>

      <Card padded={false}>
        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-4">
            <ErrorState message="Could not load sales." onRetry={() => refetch()} />
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<ShoppingCart className="size-10" />}
            title={search || status || source ? 'No sales match your filters' : 'No sales yet'}
            description={
              search || status || source
                ? 'Try clearing a filter.'
                : 'Record your first sale to start tracking revenue.'
            }
            action={
              mayRecord && !search && !status && !source ? (
                <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                  Record sale
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Table on desktop */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left">
                  <tr>
                    <Th>Reference</Th>
                    <Th>Date</Th>
                    <Th>Description</Th>
                    <Th>Customer</Th>
                    <Th align="right">Amount</Th>
                    <Th align="right">Outstanding</Th>
                    <Th>Status</Th>
                    <Th>Source</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.data.map((sale) => (
                    <tr key={sale.id} className="hover:bg-slate-50">
                      <Td className="font-mono text-xs text-slate-500">{sale.reference}</Td>
                      <Td className="whitespace-nowrap">{date(sale.occurredAt, timezone)}</Td>
                      <Td className="max-w-xs truncate">{sale.description ?? '—'}</Td>
                      <Td className="max-w-[10rem] truncate">{sale.customerName ?? '—'}</Td>
                      <Td align="right" className="font-medium tabular">
                        {money(sale.amount, sale.currencyCode)}
                      </Td>
                      <Td align="right" className="tabular">
                        {sale.amountOutstanding === '0.00' ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className="font-medium text-amber-700">
                            {money(sale.amountOutstanding, sale.currencyCode)}
                          </span>
                        )}
                      </Td>
                      <Td>
                        <Badge tone={STATUS_TONES[sale.paymentStatus]}>
                          {PAYMENT_STATUS_LABELS[sale.paymentStatus]}
                        </Badge>
                      </Td>
                      <Td className="text-slate-500">{SALE_SOURCE_LABELS[sale.source]}</Td>
                      <Td>
                        <RowActions
                          sale={sale}
                          canPay={can('payments:create')}
                          canVoid={can('sales:void')}
                          onPay={() => setPayingFor(sale)}
                          onVoid={() => setVoiding(sale)}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards on mobile — a nine-column table is unusable on a phone. */}
            <ul className="divide-y divide-slate-100 md:hidden">
              {data.data.map((sale) => (
                <li key={sale.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">
                        {sale.description ?? sale.reference}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-slate-500">
                        {date(sale.occurredAt, timezone)}
                        {sale.customerName && ` · ${sale.customerName}`}
                      </p>
                    </div>
                    <p className="shrink-0 font-semibold tabular text-slate-900">
                      {money(sale.amount, sale.currencyCode)}
                    </p>
                  </div>

                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Badge tone={STATUS_TONES[sale.paymentStatus]}>
                      {PAYMENT_STATUS_LABELS[sale.paymentStatus]}
                    </Badge>
                    <Badge>{SALE_SOURCE_LABELS[sale.source]}</Badge>
                    {sale.amountOutstanding !== '0.00' && (
                      <span className="text-xs font-medium tabular text-amber-700">
                        {money(sale.amountOutstanding, sale.currencyCode)} owed
                      </span>
                    )}
                    <div className="ml-auto">
                      <RowActions
                        sale={sale}
                        canPay={can('payments:create')}
                        canVoid={can('sales:void')}
                        onPay={() => setPayingFor(sale)}
                        onVoid={() => setVoiding(sale)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <Pagination
              page={data.meta.page}
              pageSize={data.meta.pageSize}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Modal open={creating} onClose={() => setCreating(false)} title="Record a sale" size="lg">
        <SaleForm
          onDone={() => {
            setCreating(false);
            invalidate();
          }}
          onCancel={() => setCreating(false)}
        />
      </Modal>

      <Modal
        open={Boolean(payingFor)}
        onClose={() => setPayingFor(null)}
        title={`Record payment · ${payingFor?.reference ?? ''}`}
      >
        {payingFor && (
          <RecordPaymentForm
            sale={payingFor}
            onDone={() => {
              setPayingFor(null);
              invalidate();
            }}
            onCancel={() => setPayingFor(null)}
          />
        )}
      </Modal>

      <Modal open={Boolean(voiding)} onClose={() => setVoiding(null)} title="Void this sale">
        {voiding && (
          <VoidForm
            sale={voiding}
            currency={currency}
            pending={voidSale.isPending}
            error={voidSale.error instanceof Error ? voidSale.error.message : null}
            onConfirm={(reason) => voidSale.mutate({ id: voiding.id, reason })}
            onCancel={() => setVoiding(null)}
          />
        )}
      </Modal>
    </>
  );
}

function RowActions({
  sale,
  canPay,
  canVoid,
  onPay,
  onVoid,
}: {
  sale: Sale;
  canPay: boolean;
  canVoid: boolean;
  onPay: () => void;
  onVoid: () => void;
}): ReactNode {
  const settled = sale.amountOutstanding === '0.00';
  const dead = sale.paymentStatus === 'void';

  return (
    <div className="flex justify-end gap-1">
      {canPay && !settled && !dead && (
        <Button variant="ghost" size="sm" icon={<Wallet className="size-4" />} onClick={onPay}>
          <span className="hidden lg:inline">Pay</span>
        </Button>
      )}
      {canVoid && !dead && (
        <Button
          variant="ghost"
          size="sm"
          icon={<Ban className="size-4" />}
          onClick={onVoid}
          aria-label={`Void sale ${sale.reference}`}
        />
      )}
    </div>
  );
}

function VoidForm({
  sale,
  currency,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  sale: Sale;
  currency: string;
  pending: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}): ReactNode {
  const [reason, setReason] = useState('');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onConfirm(reason);
      }}
      className="space-y-4"
    >
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
        <p className="text-sm text-amber-900">
          Voiding removes {money(sale.amount, currency)} from your revenue figures. The record
          itself is kept, with your reason, so the change stays traceable.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Input
        label="Reason"
        required
        autoFocus
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="e.g. Recorded twice by mistake"
      />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="danger" loading={pending} disabled={!reason.trim()}>
          Void sale
        </Button>
      </div>
    </form>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
}): ReactNode {
  return (
    <th
      scope="col"
      className={`px-4 py-2.5 text-xs font-medium text-slate-600 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  className = '',
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
}): ReactNode {
  return (
    <td className={`px-4 py-3 ${align === 'right' ? 'text-right' : ''} ${className}`}>
      {children}
    </td>
  );
}
